/** Matches Register.jsx email domain rule (user@domain.com) */
const CAPACITY_EMAIL_DOMAIN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.com$/;

export const CAPACITY_EMAIL_ERROR_MESSAGE = 'Invalid email address';

/** Empty email is allowed; non-empty values must match Register email validation */
export function isValidCapacityEmail(email) {
  const v = String(email ?? '').trim();
  if (!v) return true;
  return CAPACITY_EMAIL_DOMAIN.test(v);
}

export function isBucketAssigneeName(name) {
  const n = String(name || '')
    .trim()
    .toLowerCase();
  return !n || n === 'unassigned' || n === 'none' || n === 'n/a';
}

export function isUnassignedBucketName(name) {
  const n = String(name || '')
    .trim()
    .toLowerCase();
  return n === 'unassigned';
}

export function shouldShowUnassignedCapacityApiAssignee(assignee, isHoursBased = null) {
  if (!isUnassignedBucketName(assignee?.assignee)) return true;
  const tickets = Number(assignee?.tickets ?? 0);
  if (tickets > 0) return true;
  // Also show when Unassigned was carried onto the plan (present even with 0 tickets).
  if (assignee?.presentInPlan !== 'no' && assignee?.presentInPlan !== false) {
    if (
      assignee?.sprintOrReleaseUser === 'yes'
      || Number(assignee?.availableHours || 0) > 0
      || assignee?.addedManually === 'yes'
      || assignee?.addedManually === true
    ) {
      return true;
    }
  }
  const hours = Number(assignee?.allocatedHours ?? 0);
  const storyPoints = Number(assignee?.allocatedStoryPoints ?? assignee?.allocatedHours ?? 0);
  if (isHoursBased === true) return hours > 0;
  if (isHoursBased === false) return storyPoints > 0 || tickets > 0;
  return hours > 0 || storyPoints > 0 || tickets > 0;
}

export function shouldShowUnassignedCapacityGridRow(row, isHoursBased) {
  if (!isUnassignedBucketName(row?.name)) return true;
  if (Number(row?.tickets ?? 0) > 0) return true;
  // Carried Unassigned with no tickets stays visible until user removes it.
  if (row?.presentInPlan !== 'no' && row?.presentInPlan !== false) {
    if (
      row?.sprintOrReleaseUser === 'yes'
      || Number(row?.availableHours || 0) > 0
      || row?.manuallyAdded === true
      || row?.addedManually === 'yes'
      || row?.addedManually === true
    ) {
      return true;
    }
  }
  const alloc = isHoursBased
    ? Number(row.allocatedHours ?? 0)
    : Number(row.allocatedStoryPoints ?? 0);
  return alloc > 0;
}

/** True / 'yes' / 'true' / case-insensitive 'yes' — used for API string flags. */
function isAffirmativeFlag(value) {
  return (
    value === true ||
    value === 'yes' ||
    value === 'true' ||
    (typeof value === 'string' && value.toLowerCase() === 'yes')
  );
}

export function isManuallyAddedCapacityRow(row) {
  if (!row || typeof row !== 'object') return false;
  return row.manuallyAdded === true || isAffirmativeFlag(row.addedManually);
}

export function isSprintOrReleaseLockedRow(row) {
  if (!row || typeof row !== 'object') return false;
  return isAffirmativeFlag(row.sprintOrReleaseUser);
}

/** Lock Add-user modal when assignee has one or more in-scope tickets (not estimate alone). */
export function isAddUserModalLockedByAllocation(row, isHoursBased = false) {
  void isHoursBased;
  if (!row || typeof row !== 'object') return false;
  if (isManuallyAddedCapacityRow(row)) return false;
  return assigneeHasScopeTickets(row);
}

export function assigneeHasCarriedAddUserModalLock(row) {
  return row?.addUserModalLocked === 'yes' || row?.addUserModalLocked === true;
}

/** True when assignee has at least one in-scope ticket (including 0 OE/SP). */
export function assigneeHasScopeTickets(row) {
  if (!row || typeof row !== 'object') return false;
  return Number(row.tickets || 0) > 0;
}

/**
 * True when this scope has an Add Users selection (not ticket-derived roster alone).
 * Ticket people with sprintOrReleaseUser=yes do not count.
 */
export function hasSavedCapacitySelectionPlan(assignees = []) {
  if (!Array.isArray(assignees) || assignees.length === 0) return false;
  return assignees.some((row) => {
    if (!row || typeof row !== 'object') return false;
    if (row.addedManually === 'yes' || row.addedManually === true) return true;
    if (row.presentInPlan === 'no' || row.presentInPlan === false) return true;
    if (row.planLockedLocally === 'yes' || row.planLockedLocally === true) return true;
    if (row.modified === true) return true;
    if (
      row.presentInPlan !== 'no'
      && row.presentInPlan !== false
      && row.sprintOrReleaseUser === 'yes'
      && Number(row.tickets || 0) === 0
    ) {
      return true;
    }
    return false;
  });
}

/** Saved future/unreleased plan must not be replaced by prior sprint on refresh. */
export function shouldKeepStoredFutureCapacityPlan(scopeDoc, assignees = null) {
  if (scopeDoc?.assigneeCopiedForToday) return true;
  return hasSavedCapacitySelectionPlan(assignees || scopeDoc?.assignees);
}

/** Effective lock on a row: tickets > 0 (manual users never locked). Stale carried lock ignored when tickets === 0. */
export function isEffectiveAddUserModalLocked(row, isHoursBased = false) {
  void isHoursBased;
  if (!row || typeof row !== 'object') return false;
  if (isManuallyAddedCapacityRow(row)) return false;
  return assigneeHasScopeTickets(row);
}

/** True when a sprint/release assignee list can seed carry-forward (hard sync already established). */
export function hasCapacityPlanSourceAssignees(assignees = []) {
  if (!Array.isArray(assignees) || assignees.length === 0) return false;
  if (hasSavedCapacitySelectionPlan(assignees)) return true;
  if (assigneesHaveAvailableHours(assignees)) return true;
  return assignees.some((row) => assigneeHasScopeTickets(row) || assigneeHasSyncedAllocation(row));
}

/** Prior sprint/release with an established capacity plan — mirrors API carry-forward source resolution. */
export function resolvePriorSprintPlanSource(sprintList = [], selectedSprint = null) {
  if (!Array.isArray(sprintList) || sprintList.length === 0) return null;

  const sorted = [...sprintList].sort(
    (a, b) => new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime(),
  );
  const selectedState = String(selectedSprint?.state || '').toLowerCase();
  const selectedId = String(selectedSprint?._id || selectedSprint?.id || '');
  const selectedIdx = selectedId
    ? sorted.findIndex((sprint) => String(sprint?._id || sprint?.id) === selectedId)
    : -1;

  if (selectedState === 'future') {
    const activeSprint = sorted.find((sprint) => {
      const state = String(sprint?.state || '').toLowerCase();
      return state === 'active' || state === 'current';
    });
    if (activeSprint && hasSavedCapacitySelectionPlan(activeSprint.assignees)) {
      return activeSprint;
    }
    const startIdx = selectedIdx > 0 ? selectedIdx - 1 : sorted.length - 1;
    for (let i = startIdx; i >= 0; i -= 1) {
      if (hasSavedCapacitySelectionPlan(sorted[i]?.assignees)) {
        return sorted[i];
      }
    }
    if (activeSprint && hasCapacityPlanSourceAssignees(activeSprint.assignees)) {
      return activeSprint;
    }
  }

  const startIdx = selectedIdx > 0 ? selectedIdx - 1 : sorted.length - 1;
  for (let i = startIdx; i >= 0; i -= 1) {
    if (hasSavedCapacitySelectionPlan(sorted[i]?.assignees)) {
      return sorted[i];
    }
  }
  for (let i = startIdx; i >= 0; i -= 1) {
    if (hasCapacityPlanSourceAssignees(sorted[i]?.assignees)) {
      return sorted[i];
    }
  }

  return null;
}

export function resolvePriorReleasePlanSource(releaseList = [], selectedRelease = null) {
  if (!Array.isArray(releaseList) || releaseList.length === 0) return null;

  const sorted = sortReleasesForCarryForward(releaseList);
  const selectedId = String(selectedRelease?._id || selectedRelease?.id || '');
  const selectedIdx = selectedId
    ? sorted.findIndex((release) => String(release?._id || release?.id) === selectedId)
    : -1;
  if (selectedIdx <= 0) return null;

  for (let i = selectedIdx - 1; i >= 0; i -= 1) {
    if (hasSavedCapacitySelectionPlan(sorted[i]?.assignees)) {
      return sorted[i];
    }
  }
  for (let i = selectedIdx - 1; i >= 0; i -= 1) {
    if (hasCapacityPlanSourceAssignees(sorted[i]?.assignees)) {
      return sorted[i];
    }
  }

  return null;
}

function mergeCarryForwardPlanFieldsUi(targetRow = {}, sourceRow = {}, isHoursBased = false) {
  void isHoursBased;
  if (targetRow?.modified === true) {
    return targetRow;
  }
  const targetSnapshot = targetRow?.initialPlannedAllocation;
  const tickets = Number(targetRow.tickets || 0);
  const merged = {
    ...targetRow,
    email: String(sourceRow.email || targetRow.email || '').trim(),
    availableHours: Number(sourceRow.availableHours ?? targetRow.availableHours ?? 0),
    role: sourceRow.role || targetRow.role || '',
    billingRate: Number(sourceRow.billingRate ?? targetRow.billingRate ?? 0),
    allocationType: sourceRow.allocationType || targetRow.allocationType || '',
    // holiday / leaves / net are scope-specific — do not copy leave-adjusted net from prior
    holiday: Number(targetRow.holiday ?? 0),
    leaves: Number(targetRow.leaves ?? 0),
    netAvailableCapacity: Number(
      sourceRow.availableHours ?? sourceRow.netAvailableCapacity ?? targetRow.availableHours ?? 0,
    ),
    presentInPlan: sourceRow.presentInPlan === 'no' ? 'no' : 'yes',
    addedManually:
      sourceRow.addedManually === 'yes' || targetRow.addedManually === 'yes' ? 'yes' : 'no',
    sprintOrReleaseUser:
      sourceRow.sprintOrReleaseUser === 'yes' || targetRow.sprintOrReleaseUser === 'yes'
        ? 'yes'
        : 'no',
    tickets,
    // Lock from current-scope tickets only.
    addUserModalLocked: tickets > 0 ? 'yes' : 'no',
    modified: false,
  };
  // initialPlannedAllocation is scope-specific — never carry from prior sprint/release
  if (
    targetSnapshot !== null
    && targetSnapshot !== undefined
    && Number.isFinite(Number(targetSnapshot))
  ) {
    merged.initialPlannedAllocation = Number(targetSnapshot);
  } else {
    delete merged.initialPlannedAllocation;
  }
  return merged;
}

function isAssigneeCapacityModified(row) {
  return row?.modified === true;
}

function isFutureLocalOnlyCapacityRow(targetRow, sourceRow = null) {
  if (isAssigneeCapacityModified(targetRow)) return true;
  if (targetRow?.planLockedLocally === 'yes' || targetRow?.planLockedLocally === true) return true;
  const isManual = targetRow?.addedManually === 'yes' || targetRow?.addedManually === true;
  if (isManual && !sourceRow) return true;
  return false;
}

function isAssigneeOnCarrySourcePlan(row) {
  return row?.presentInPlan !== 'no' && row?.presentInPlan !== false;
}

function fillEmptyCarryForwardPlanFieldsUi(targetRow = {}, sourceRow = {}) {
  if (
    !sourceRow
    || targetRow?.modified === true
    || targetRow?.planLockedLocally === 'yes'
    || targetRow?.planLockedLocally === true
  ) {
    return targetRow;
  }
  const targetHours = Number(targetRow.availableHours ?? 0);
  const sourceHours = Number(sourceRow.availableHours ?? 0);
  const targetNet = Number(targetRow.netAvailableCapacity ?? 0);
  const targetRate = Number(targetRow.billingRate ?? 0);
  const sourceRate = Number(sourceRow.billingRate ?? 0);
  return {
    ...targetRow,
    email: String(targetRow.email || sourceRow.email || '').trim(),
    availableHours: targetHours > 0 ? targetHours : sourceHours,
    role: String(targetRow.role || '').trim() || sourceRow.role || '',
    billingRate: targetRate > 0 ? targetRate : sourceRate,
    allocationType: String(targetRow.allocationType || '').trim() || sourceRow.allocationType || '',
    netAvailableCapacity: targetHours > 0 || targetNet > 0
      ? Number(targetRow.netAvailableCapacity ?? targetHours)
      : Number(sourceRow.availableHours ?? sourceRow.netAvailableCapacity ?? 0),
    addedManually:
      sourceRow.addedManually === 'yes' || targetRow.addedManually === 'yes' ? 'yes' : 'no',
    sprintOrReleaseUser:
      sourceRow.sprintOrReleaseUser === 'yes' || targetRow.sprintOrReleaseUser === 'yes'
        ? 'yes'
        : 'no',
    presentInPlan: isAssigneeOnCarrySourcePlan(sourceRow)
      ? 'yes'
      : (targetRow.presentInPlan === 'no' ? 'no' : 'yes'),
  };
}

/**
 * Future scope was saved directly: keep existing numbers, still add missing active-plan users
 * and fill empty capacity/role/email from the previous sprint.
 */
export function mergeActivePlanIntoModifiedFuture(
  assignees = [],
  sourceAssignees = [],
  isHoursBased = false,
) {
  if (!Array.isArray(sourceAssignees) || sourceAssignees.length === 0) {
    return Array.isArray(assignees) ? assignees : [];
  }
  const sourceByNorm = new Map();
  for (const sourceRow of sourceAssignees) {
    const name = String(sourceRow?.assignee || '').trim();
    if (!name) continue;
    sourceByNorm.set(name.toLowerCase(), sourceRow);
  }
  const merged = [];
  const mergedKeys = new Set();
  for (const targetRow of assignees || []) {
    const key = String(targetRow?.assignee || '').trim().toLowerCase();
    if (!key) continue;
    const sourceRow = sourceByNorm.get(key);
    const isManual = targetRow?.addedManually === 'yes' || targetRow?.addedManually === true;
    if (isManual && !sourceRow) {
      merged.push(targetRow);
    } else if (sourceRow && isAssigneeOnCarrySourcePlan(sourceRow)) {
      const targetOffPlan = targetRow.presentInPlan === 'no' || targetRow.presentInPlan === false;
      if (targetOffPlan) {
        const promoted = {
          ...targetRow,
          presentInPlan: 'yes',
          planLockedLocally: 'no',
        };
        merged.push({
          ...fillEmptyCarryForwardPlanFieldsUi(promoted, sourceRow),
          presentInPlan: 'yes',
          planLockedLocally: 'no',
        });
      } else if (targetRow?.modified === true) {
        merged.push(targetRow);
      } else {
        merged.push(fillEmptyCarryForwardPlanFieldsUi(targetRow, sourceRow));
      }
    } else {
      merged.push(targetRow);
    }
    mergedKeys.add(key);
  }
  for (const sourceRow of sourceAssignees) {
    const key = String(sourceRow?.assignee || '').trim().toLowerCase();
    if (!key || mergedKeys.has(key) || !isAssigneeOnCarrySourcePlan(sourceRow)) continue;
    appendCarriedAssigneeRowUi(merged, sourceRow, 0, isHoursBased);
    mergedKeys.add(key);
  }
  return merged.map((row) => ({
    ...row,
    addUserModalLocked: isEffectiveAddUserModalLocked(row, isHoursBased) ? 'yes' : 'no',
  }));
}

function isActiveSprintPlanSource(sourceSprint) {
  const state = String(sourceSprint?.state || '').toLowerCase();
  return state === 'active' || state === 'current';
}

export function isUnreleasedRelease(release) {
  return String(release?.status || '').toLowerCase() === 'unreleased';
}

/** Prior unreleased release can live-mirror into later unreleased. */
export function isLiveReleasePlanSource(sourceRelease) {
  return isUnreleasedRelease(sourceRelease);
}

/**
 * Target receives live sequential carry when a prior unreleased release exists
 * (independent of startDate — supports overlapping started releases).
 */
export function isSequentialReleaseLiveCarryTarget(releaseList = [], targetRelease = null) {
  if (!isUnreleasedRelease(targetRelease) || !Array.isArray(releaseList) || releaseList.length === 0) {
    return false;
  }
  const sorted = sortReleasesForCarryForward(releaseList);
  const targetId = String(targetRelease?._id || targetRelease?.id || '');
  const targetIdx = targetId
    ? sorted.findIndex((release) => String(release?._id || release?.id) === targetId)
    : -1;
  if (targetIdx <= 0) return false;
  for (let i = targetIdx - 1; i >= 0; i -= 1) {
    if (isUnreleasedRelease(sorted[i])) return true;
  }
  return false;
}

function appendCarriedAssigneeRowUi(merged, sourceRow, scopeTickets = 0, isHoursBased = false) {
  merged.push({
    ...sourceRow,
    allocatedHours: Number(sourceRow.allocatedHours || 0),
    allocatedStoryPoints: Number(sourceRow.allocatedStoryPoints || 0),
    tickets: Number(scopeTickets || 0),
    holiday: 0,
    leaves: 0,
    planLockedLocally: 'no',
    modified: false,
    addUserModalLocked: Number(scopeTickets || 0) > 0 ? 'yes' : 'no',
  });
  delete merged[merged.length - 1].initialPlannedAllocation;
  void isHoursBased;
}

/**
 * Active → future: mirror add/update/remove unless the future assignee has modified=true.
 */
export function applyCarryForwardActivePlanToFuture(
  assignees = [],
  sourceAssignees = [],
  isHoursBased = false,
) {
  if (!Array.isArray(sourceAssignees) || sourceAssignees.length === 0) {
    return Array.isArray(assignees) ? assignees : [];
  }
  const sourceByNorm = new Map();
  for (const sourceRow of sourceAssignees) {
    const name = String(sourceRow?.assignee || '').trim();
    if (!name) continue;
    sourceByNorm.set(name.toLowerCase(), sourceRow);
  }

  const merged = [];
  const mergedKeys = new Set();
  for (const targetRow of assignees || []) {
    const key = String(targetRow?.assignee || '').trim().toLowerCase();
    if (!key) continue;
    const sourceRow = sourceByNorm.get(key);
    if (isFutureLocalOnlyCapacityRow(targetRow, sourceRow)) {
      merged.push(targetRow);
      mergedKeys.add(key);
      continue;
    }
    const scopeTickets = Number(targetRow.tickets || 0);
    if (!sourceRow) {
      if (scopeTickets > 0
        || targetRow?.addedManually === 'yes'
        || targetRow?.addedManually === true) {
        merged.push({
          ...targetRow,
          addUserModalLocked: isEffectiveAddUserModalLocked(targetRow, isHoursBased) ? 'yes' : 'no',
        });
        mergedKeys.add(key);
      }
      continue;
    }
    if (!isAssigneeOnCarrySourcePlan(sourceRow) && scopeTickets <= 0) {
      merged.push(mergeCarryForwardPlanFieldsUi(targetRow, sourceRow, isHoursBased));
      mergedKeys.add(key);
      continue;
    }
    if (!isAssigneeOnCarrySourcePlan(sourceRow) && scopeTickets > 0) {
      merged.push({
        ...mergeCarryForwardPlanFieldsUi(targetRow, sourceRow, isHoursBased),
        presentInPlan: 'yes',
        addUserModalLocked: 'yes',
      });
      mergedKeys.add(key);
      continue;
    }
    merged.push(mergeCarryForwardPlanFieldsUi(targetRow, sourceRow, isHoursBased));
    mergedKeys.add(key);
  }

  for (const sourceRow of sourceAssignees) {
    const key = String(sourceRow?.assignee || '').trim().toLowerCase();
    if (!key || mergedKeys.has(key) || !isAssigneeOnCarrySourcePlan(sourceRow)) continue;
    appendCarriedAssigneeRowUi(merged, sourceRow, 0, isHoursBased);
    mergedKeys.add(key);
  }
  return merged.map((row) => ({
    ...row,
    addUserModalLocked: isEffectiveAddUserModalLocked(row, isHoursBased) ? 'yes' : 'no',
  }));
}

/**
 * After hard sync on a prior sprint/release, carry the full capacity plan onto the target roster.
 * Lock is always recomputed from current-scope tickets (not prior lock).
 */
export function applyCarryForwardCapacityPlanToAssignees(
  assignees = [],
  sourceAssignees = [],
  isHoursBased = false,
) {
  if (!Array.isArray(assignees)) return [];
  const hasPlanSource = hasCapacityPlanSourceAssignees(sourceAssignees);
  if (!hasPlanSource) {
    return assignees.map((row) => ({
      ...row,
      addUserModalLocked: isEffectiveAddUserModalLocked(row, isHoursBased) ? 'yes' : 'no',
    }));
  }

  const sourceByNorm = new Map();
  for (const sourceRow of sourceAssignees) {
    const name = String(sourceRow?.assignee || '').trim();
    if (!name) continue;
    sourceByNorm.set(name.toLowerCase(), sourceRow);
  }

  const merged = assignees.map((targetRow) => {
    if (targetRow?.modified === true) {
      return targetRow;
    }
    const key = String(targetRow?.assignee || '').trim().toLowerCase();
    const sourceRow = sourceByNorm.get(key);
    if (!sourceRow) {
      return {
        ...targetRow,
        addUserModalLocked: isEffectiveAddUserModalLocked(targetRow, isHoursBased) ? 'yes' : 'no',
      };
    }
    return mergeCarryForwardPlanFieldsUi(targetRow, sourceRow, isHoursBased);
  });

  const targetKeys = new Set(
    merged
      .map((row) => String(row?.assignee || '').trim().toLowerCase())
      .filter(Boolean),
  );
  for (const sourceRow of sourceAssignees) {
    const key = String(sourceRow?.assignee || '').trim().toLowerCase();
    // Include Unassigned in carry-forward (scenario 7).
    if (!key || targetKeys.has(key)) continue;
    merged.push({
      ...sourceRow,
      allocatedHours: Number(sourceRow.allocatedHours || 0),
      allocatedStoryPoints: Number(sourceRow.allocatedStoryPoints || 0),
      tickets: 0,
      holiday: 0,
      leaves: 0,
      planLockedLocally: 'no',
      addUserModalLocked: 'no',
    });
    delete merged[merged.length - 1].initialPlannedAllocation;
    targetKeys.add(key);
  }

  return merged;
}

/** Append prior-plan assignees missing from target without overwriting saved capacity fields. */
export function unionMissingCarryForwardAssignees(targetAssignees = [], sourceAssignees = []) {
  const merged = Array.isArray(targetAssignees) ? [...targetAssignees] : [];
  const targetKeys = new Set(
    merged.map((row) => String(row?.assignee || '').trim().toLowerCase()).filter(Boolean),
  );
  for (const sourceRow of sourceAssignees || []) {
    const key = String(sourceRow?.assignee || '').trim().toLowerCase();
    if (!key || targetKeys.has(key)) continue;
    merged.push({
      ...sourceRow,
      allocatedHours: Number(sourceRow.allocatedHours || 0),
      allocatedStoryPoints: Number(sourceRow.allocatedStoryPoints || 0),
      tickets: 0,
      holiday: 0,
      leaves: 0,
      planLockedLocally: 'no',
      addUserModalLocked: 'no',
    });
    delete merged[merged.length - 1].initialPlannedAllocation;
    targetKeys.add(key);
  }
  return merged.map((row) => ({
    ...row,
    addUserModalLocked: assigneeHasScopeTickets(row) ? 'yes' : 'no',
  }));
}

/** Modal lock: tickets on current sprint/release only (not stale carried flag). */
export function isAddUserModalLocked(row, isHoursBased = false) {
  if (!row || typeof row !== 'object') return false;
  if (isManuallyAddedCapacityRow(row)) return false;
  return assigneeHasScopeTickets(row) || isAddUserModalLockedByAllocation(row, isHoursBased);
}

export function isAddUserModalLockedAssignee(assigneeRow, isHoursBased = false) {
  if (!assigneeRow || typeof assigneeRow !== 'object') return false;
  if (assigneeRow.addedManually === 'yes' || assigneeRow.addedManually === true) return false;
  return assigneeHasScopeTickets(assigneeRow) || isAddUserModalLockedByAllocation(assigneeRow, isHoursBased);
}

export function resolveAddUserModalLockedForSave(row, isHoursBased = false) {
  return isAddUserModalLocked(row, isHoursBased) ? 'yes' : 'no';
}

/** True when assignee has Jira-synced allocated work on the sprint/release. */
export function assigneeHasSyncedAllocation(row, isHoursBased = null) {
  if (!row || typeof row !== 'object') return false;
  const hours = Number(row.allocatedHours || 0);
  const points = Number(row.allocatedStoryPoints ?? row.storyPoints ?? 0);
  if (isHoursBased === true) return hours > 0;
  if (isHoursBased === false) return points > 0 || hours > 0;
  return hours > 0 || points > 0;
}

/** Effective on-plan flag: explicit removals stay off unless they have scope tickets. */
export function resolveCapacityPresentInPlan(row, isHoursBased = null) {
  void isHoursBased;
  if (!row || typeof row !== 'object') return 'yes';
  if (row.presentInPlan !== 'no' && row.presentInPlan !== false) return 'yes';
  return assigneeHasScopeTickets(row) ? 'yes' : 'no';
}

/** Assignees included in Stand Up developer dropdown and capacity summary (excludes Add-user removals). */
export function isAssigneeOnCapacityPlan(row, isHoursBased = null) {
  if (!row || typeof row !== 'object') return true;
  return resolveCapacityPresentInPlan(row, isHoursBased) === 'yes';
}

export function pickCapacityPresentInPlan(...values) {
  for (const value of values) {
    if (value === 'no' || value === false) {
      return 'no';
    }
  }
  return 'yes';
}

export function filterAssigneesForCapacityPlanView(assigneesList, isHoursBased = null) {
  if (!Array.isArray(assigneesList)) return [];
  return assigneesList.filter((a) => {
    const name = String(a?.assignee || '').trim();
    if (!name) return false;
    if (isUnassignedBucketName(name)) {
      return shouldShowUnassignedCapacityApiAssignee(a, isHoursBased);
    }
    if (!isAssigneeOnCapacityPlan(a, isHoursBased)) return false;
    if (isBucketAssigneeName(name)) return false;
    return true;
  });
}

export function toNumericEstimate(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const s = String(value).trim();
  if (!s || s.toLowerCase() === 'na' || s.toLowerCase() === 'invalid number') return 0;
  const direct = Number(s);
  if (Number.isFinite(direct)) return direct;
  const m = s.match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : 0;
}

/** Inherit Hrs vs SP from the prior sprint for 2nd+ future sprints only (not the first future). */
export function resolvePriorSprintHoursMode(sprintList = [], selectedSprint = null) {
  if (!selectedSprint || !Array.isArray(sprintList) || sprintList.length === 0) {
    return null;
  }
  const sorted = [...sprintList].sort(
    (a, b) => new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime(),
  );
  const selectedId = String(selectedSprint?._id || selectedSprint?.id || '');
  const selectedIdx = sorted.findIndex(
    (sprint) => String(sprint?._id || sprint?.id) === selectedId,
  );
  if (selectedIdx <= 0) {
    return null;
  }
  const futuresBefore = sorted
    .slice(0, selectedIdx)
    .filter((sprint) => String(sprint?.state || '').toLowerCase() === 'future');
  if (futuresBefore.length === 0) {
    return null;
  }
  const prior = sorted[selectedIdx - 1];
  return typeof prior?.hours === 'boolean' ? prior.hours : null;
}

/**
 * Resolve Hrs vs SP for stand-up — future sprints inherit from the prior sprint (active, closed,
 * or previous future); otherwise trust synced sprint.hours.
 */
export function resolveStandupHoursBasedProject(
  currentData,
  { sprintList = [] } = {},
) {
  const sprintState = String(currentData?.state || '').toLowerCase();
  if (sprintState === 'future' && sprintList.length > 0) {
    const priorMode = resolvePriorSprintHoursMode(sprintList, currentData);
    if (priorMode !== null) {
      return priorMode;
    }
  }

  if (typeof currentData?.hours === 'boolean') {
    return currentData.hours;
  }

  const pointsSourceTypeRaw = currentData?.pointsSourceType;
  const effectivePointsSourceType = pointsSourceTypeRaw ?? 'storyPoints';
  const metrics = currentData?.committedVsCompletedMetrics || {};
  const hasCommittedHours =
    Number(metrics.committedHours) > 0 ||
    Number(metrics.completedHours) > 0 ||
    Number(metrics.plannedOriginalEstimateHrs) > 0;
  const hasCommittedStoryPoints =
    Number(metrics.committedStoryPoints) > 0 ||
    Number(metrics.completedStoryPoints) > 0 ||
    Number(metrics.plannedStoryPoints) > 0;

  if (effectivePointsSourceType !== 'storyPoints' && hasCommittedHours) {
    return true;
  }
  if (hasCommittedHours && !hasCommittedStoryPoints) {
    return true;
  }
  if (hasCommittedStoryPoints && !hasCommittedHours) {
    return false;
  }
  const assignees = Array.isArray(currentData?.assignees) ? currentData.assignees : [];
  const hasAllocHours = assignees.some((a) => Number(a?.allocatedHours) > 0);
  const hasAllocStoryPoints = assignees.some((a) => Number(a?.allocatedStoryPoints) > 0);
  if (hasAllocHours && !hasAllocStoryPoints) {
    return true;
  }
  if (hasAllocStoryPoints && !hasAllocHours) {
    return false;
  }
  return false;
}

/** Prefer Redux selected Sprint/Release when id matches (fresh after dropdown switch), else list row. */
export function resolveStandupScopeData({
  selectedValue,
  selectedSprint,
  selectedRelease,
  sprintList = [],
  releaseList = [],
  jiraData = {},
} = {}) {
  const isRelease = selectedValue?.value === 'Release' || selectedValue?.value === 'release';
  if (isRelease) {
    const selectedId = String(selectedRelease?.id || '');
    const fromRedux = jiraData?.Release;
    if (fromRedux && selectedId && String(fromRedux._id) === selectedId) {
      return fromRedux;
    }
    const fromList = (releaseList || []).find((r) => String(r?._id) === selectedId);
    return fromList ?? fromRedux ?? jiraData?.Sprint ?? null;
  }
  const selectedId = String(selectedSprint?.id || '');
  const fromRedux = jiraData?.Sprint;
  if (fromRedux && selectedId && String(fromRedux._id) === selectedId) {
    return fromRedux;
  }
  const fromList = (sprintList || []).find((s) => String(s?._id) === selectedId);
  return fromList ?? fromRedux ?? jiraData?.Release ?? null;
}

export function lookupIssueEstimateForAssignee(assigneeName, issueEstimatedByAssignee) {
  const key = String(assigneeName || '').trim();
  if (!key || !issueEstimatedByAssignee) {
    return 0;
  }
  if (issueEstimatedByAssignee.has(key)) {
    return Number(issueEstimatedByAssignee.get(key) || 0);
  }
  if (isUnassignedBucketName(key)) {
    return Number(issueEstimatedByAssignee.get('Unassigned') || 0);
  }
  return 0;
}

function isCapacityExcludedIssueType(typeName) {
  const normalized = String(typeName || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  return normalized === 'epic';
}

/** All in-scope issues count toward allocated capacity (sub-tasks included); epics excluded. */
export function filterIssuesForCapacityAllocation(issues = []) {
  const list = Array.isArray(issues) ? issues : [];
  return list.filter((issue) => {
    const typeName = issue?.type?.name || issue?.type;
    return !isCapacityExcludedIssueType(typeName);
  });
}

export function buildIssueEstimateTotalsByAssignee(issues = [], isHoursBased = false) {
  const totals = new Map();
  for (const issue of filterIssuesForCapacityAllocation(issues)) {
    const assignee = normalizeIssueAssigneeName(issue);
    const key = isUnassignedBucketName(assignee) ? 'Unassigned' : assignee;
    const estimate = issueEstimateForCapacity(issue, isHoursBased);
    if (!Number.isFinite(estimate) || estimate <= 0) {
      continue;
    }
    totals.set(key, Number(totals.get(key) || 0) + estimate);
  }
  return totals;
}

/** Count in-scope issues per assignee (including 0 OE/SP) for lock / roster presence. */
export function buildIssueTicketCountsByAssignee(issues = []) {
  const totals = new Map();
  for (const issue of filterIssuesForCapacityAllocation(issues)) {
    const assignee = normalizeIssueAssigneeName(issue);
    const key = isUnassignedBucketName(assignee) ? 'Unassigned' : assignee;
    totals.set(key, Number(totals.get(key) || 0) + 1);
  }
  return totals;
}

export function allocatedCapacityForAssignee(
  item,
  isHoursBased,
  issueEstimatedByAssignee,
  allocationMode = 'scoped',
) {
  if (allocationMode === 'initial') {
    const initial = Number(item?.initialPlannedAllocation);
    return Number.isFinite(initial) ? initial : 0;
  }
  const issueEstimated = lookupIssueEstimateForAssignee(item?.assignee, issueEstimatedByAssignee);
  if (issueEstimatedByAssignee && issueEstimatedByAssignee.size > 0) {
    return issueEstimated;
  }
  const fromPlan = isHoursBased
    ? Number(item?.allocatedHours || 0)
    : Number(item?.allocatedStoryPoints ?? 0);
  return fromPlan > 0 ? fromPlan : issueEstimated;
}

/** Team "used" total from capacity-plan assignee rows (not sprint scope / commitment). */
export function sumAllocatedFromCapacityPlanAssignees(
  assignees = [],
  isHoursBased,
  issueEstimatedByAssignee,
  allocationMode = 'scoped',
) {
  return (assignees || []).reduce(
    (sum, item) =>
      sum + allocatedCapacityForAssignee(item, isHoursBased, issueEstimatedByAssignee, allocationMode),
    0,
  );
}

/** Utilization % for gauge; 0 when no available capacity is defined. */
export function computeCapacityGaugeUtilization(available, allocated) {
  const avail = Number(available) || 0;
  const alloc = Number(allocated) || 0;
  if (avail <= 0) {
    return 0;
  }
  return Math.round((alloc / avail) * 100);
}

export function computeCapacityBarSegments(available, allocated) {
  const avail = Number(available) || 0;
  const used = Number(allocated) || 0;
  if (avail === 0) {
    if (used > 0) {
      return { greenPct: 0, redPct: 100, emptyPct: 0 };
    }
    return { greenPct: 0, redPct: 0, emptyPct: 100 };
  }
  const scale = Math.max(avail, used, 1);
  const greenPct = (Math.min(used, avail) / scale) * 100;
  const redPct = used > avail ? ((used - avail) / scale) * 100 : 0;
  const emptyPct = Math.max(0, 100 - greenPct - redPct);
  return { greenPct, redPct, emptyPct };
}

export function isCapacityOverloaded({ available, allocated, remaining, utilization }) {
  const avail = Number(available) || 0;
  const alloc = Number(allocated) || 0;
  const remain = Number(remaining);
  return remain < 0 || Number(utilization) > 100 || (avail === 0 && alloc > 0);
}

export function issueEstimateForCapacity(issue, isHoursBased) {
  if (isHoursBased) {
    const candidates = [
      issue?.originalEstimateHrs,
      issue?.originalEstimate,
      issue?.customFieldsByName?.['Original Estimate'],
      issue?.customFieldsByName?.['original estimate'],
      issue?.customFields?.originalEstimate,
      issue?.customFields?.originalEstimateHrs,
    ];
    const byName = issue?.customFieldsByName;
    if (byName && typeof byName === 'object') {
      for (const [name, val] of Object.entries(byName)) {
        const lower = String(name || '').trim().toLowerCase();
        if (lower.includes('original estimate') || lower === 'time original estimate') {
          candidates.push(val);
        }
      }
    }
    for (const raw of candidates) {
      const parsed = toNumericEstimate(raw);
      if (parsed > 0) {
        return parsed;
      }
    }
    return 0;
  }
  return toNumericEstimate(
    issue?.storyPoints
      ?? issue?.customFields?.estimate
      ?? issue?.customFieldsByName?.['Story Points']
      ?? issue?.customFieldsByName?.Estimate,
  );
}

export function normalizeIssueAssigneeName(issue) {
  const a = issue?.assignee;
  if (typeof a === 'string') {
    const t = a.trim();
    if (t) return t;
  } else if (a && typeof a === 'object') {
    const candidate = a.username || a.displayName || a.name || a.login || '';
    const t = String(candidate).trim();
    if (t) return t;
  }
  if (Array.isArray(issue?.developer) && issue.developer.length > 0) {
    const d = String(issue.developer[0] || '').trim();
    if (d) return d;
  }
  return 'Unassigned';
}

/**
 * When Sprint/Release.assignees is empty (common for GitHub until capacity sync), build rows from
 * jira table issues + project user list so Standup developer picker and capacity card work.
 */
export function buildStandupCapacityAssigneesFallback({
  issues = [],
  userList = [],
  existingAssignees = [],
  isHoursBased = false,
} = {}) {
  const byKey = new Map();
  const issueTotalsByAssignee = buildIssueEstimateTotalsByAssignee(issues, isHoursBased);
  const hasIssueTotals = issueTotalsByAssignee.size > 0;

  const ensure = (assignee) => {
    const name = String(assignee || '').trim();
    if (!name) {
      return null;
    }
    const key = name.toLowerCase();
    if (!byKey.has(key)) {
      const existing = (existingAssignees || []).find(
        (a) =>
          String(a?.assignee || '')
            .trim()
            .toLowerCase() === key,
      );
      const canonicalName = isUnassignedBucketName(name) ? 'Unassigned' : name;
      const issueTotal = issueTotalsByAssignee.get(canonicalName);
      byKey.set(key, {
        assignee: canonicalName,
        email: String(existing?.email || '').trim(),
        availableHours: Number(existing?.availableHours ?? 0),
        allocatedHours: hasIssueTotals
          ? (isHoursBased ? Number(issueTotal || 0) : Number(existing?.allocatedHours || 0))
          : Number(existing?.allocatedHours || 0),
        allocatedStoryPoints: hasIssueTotals
          ? (isHoursBased ? Number(existing?.allocatedStoryPoints || 0) : Number(issueTotal || 0))
          : Number(existing?.allocatedStoryPoints ?? 0),
        initialPlannedAllocation:
          existing?.initialPlannedAllocation !== null
          && existing?.initialPlannedAllocation !== undefined
          && Number.isFinite(Number(existing.initialPlannedAllocation))
            ? Number(existing.initialPlannedAllocation)
            : undefined,
        netAvailableCapacity: Number(existing?.netAvailableCapacity ?? 0),
        role: existing?.role || '',
        billingRate: Number(existing?.billingRate || 0),
        holiday: Number(existing?.holiday || 0),
        leaves: Number(existing?.leaves || 0),
        allocationType: existing?.allocationType || '',
        addedManually: existing?.addedManually === 'yes' ? 'yes' : 'no',
        presentInPlan: existing?.presentInPlan === 'no' ? 'no' : 'yes',
        sprintOrReleaseUser: existing?.sprintOrReleaseUser === 'yes' ? 'yes' : 'no',
      });
    }
    return byKey.get(key);
  };

  /** When capacity assignees exist, roster comes from saved plan flags — do not re-add full project userList. */
  if (!(existingAssignees || []).length) {
    for (const u of userList || []) {
      const n = String(u?.displayName || '').trim();
      if (n && !isBucketAssigneeName(n)) {
        const ensured = ensure(n);
        if (ensured && !ensured.email) {
          ensured.email = String(u?.email || u?.mail || '').trim();
        }
      }
    }
  }

  for (const a of existingAssignees || []) {
    const n = String(a?.assignee || '').trim();
    if (n) {
      ensure(n);
    }
  }

  for (const [assigneeName] of issueTotalsByAssignee.entries()) {
    ensure(assigneeName);
  }

  return Array.from(byKey.values());
}

function assigneesHaveAvailableHours(assignees = []) {
  return (assignees || []).some(
    (row) =>
      row?.sprintOrReleaseUser === 'yes'
      || Number(row?.availableHours || 0) > 0
      || Number(row?.netAvailableCapacity || 0) > 0
      || Number(row?.leaves || 0) > 0,
  );
}

/** Unique company holidays falling within sprint/release window (matches Capacity Planning grid). */
export function countSprintHolidays(holidays, sprintStart, sprintEnd) {
  if (!Array.isArray(holidays) || !sprintStart || !sprintEnd) {
    return 0;
  }
  const start = new Date(sprintStart);
  const end = new Date(sprintEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0;
  }
  const uniqueDates = new Set();
  holidays.forEach((h) => {
    const holidayDate = new Date(h?.date);
    if (Number.isNaN(holidayDate.getTime())) {
      return;
    }
    if (holidayDate >= start && holidayDate <= end) {
      uniqueDates.add(holidayDate.toISOString().split('T')[0]);
    }
  });
  return uniqueDates.size;
}

/** Apply sprint holiday count to assignees when API rows omit `holiday` (standup first load). */
export function enrichCapacityAssigneesForStandup(
  assignees = [],
  { holidays = [], startDate, endDate } = {},
) {
  const sprintHolidayCount = countSprintHolidays(holidays, startDate, endDate);
  return (assignees || []).map((row) => {
    const hasHoliday =
      row?.holiday !== undefined && row?.holiday !== null && row?.holiday !== '';
    return {
      ...row,
      holiday: hasHoliday ? Number(row.holiday) : sprintHolidayCount,
    };
  });
}

/** Standup widget: carry plan (if future/unreleased) + inject holidays before net-capacity math. */
export function enrichStandupCapacityAssigneesFromScope(assignees = [], scopeData = {}, jiraData = {}) {
  let rows = Array.isArray(assignees) ? assignees : [];
  const sprintList = Array.isArray(jiraData?.sprintList) ? jiraData.sprintList : [];
  const releaseList = Array.isArray(jiraData?.releasesList) ? jiraData.releasesList : [];
  const isHoursBased = resolveStandupHoursBasedProject(scopeData, { sprintList });

  if (String(scopeData?.state || '').toLowerCase() === 'future') {
    const sourceSprint = resolvePriorSprintPlanSource(sprintList, scopeData);
    if (sourceSprint?.assignees?.length) {
      rows = isActiveSprintPlanSource(sourceSprint)
        ? applyCarryForwardActivePlanToFuture(rows, sourceSprint.assignees, isHoursBased)
        : (!shouldKeepStoredFutureCapacityPlan(scopeData, rows)
          ? applyCarryForwardCapacityPlanToAssignees(rows, sourceSprint.assignees, isHoursBased)
          : rows);
    }
  } else if (isUnreleasedRelease(scopeData)) {
    const sourceRelease = resolvePriorReleasePlanSource(releaseList, scopeData);
    if (sourceRelease?.assignees?.length) {
      rows = isLiveReleasePlanSource(sourceRelease)
        ? applyCarryForwardActivePlanToFuture(rows, sourceRelease.assignees, isHoursBased)
        : (!shouldKeepStoredFutureCapacityPlan(scopeData, rows)
          ? applyCarryForwardCapacityPlanToAssignees(rows, sourceRelease.assignees, isHoursBased)
          : rows);
    }
  }

  let companyId = null;
  if (typeof window !== 'undefined') {
    companyId = window.sessionStorage?.getItem('companyId');
  }
  const holidays =
    (companyId && jiraData?.holidayListByCompanyId?.[companyId]) ||
    jiraData?.holidayList ||
    [];
  const startDate = scopeData?.startDate;
  const endDate =
    scopeData?.completedDate || scopeData?.endDate || scopeData?.releaseDate;
  return enrichCapacityAssigneesForStandup(rows, { holidays, startDate, endDate });
}

/** Same net formula as Capacity Planning `calculateDependentValues`. */
export function computeAssigneeNetAvailableCapacity(row, isHoursBased = false) {
  const availableHours = Math.max(Number(row?.availableHours || 0), 0);
  if (availableHours <= 0) {
    return 0;
  }
  const leaves = Math.max(Number(row?.leaves || 0), 0);
  const holiday = Math.max(Number(row?.holiday || 0), 0);
  const totalOffDays = holiday + leaves;
  if (isHoursBased) {
    return Math.max(availableHours - totalOffDays * 9, 0);
  }
  return Math.max(availableHours - totalOffDays * 1, 0);
}

/** Net capacity for standup widget — always derive from availableHours / leaves / holiday (never trust stored net). */
export function resolveAssigneeNetAvailableCapacity(row, isHoursBased = false) {
  return computeAssigneeNetAvailableCapacity(row, isHoursBased);
}

export function sumAssigneeAvailableCapacity(assignees = [], isHoursBased = false) {
  return (assignees || []).reduce(
    (sum, item) => sum + resolveAssigneeNetAvailableCapacity(item, isHoursBased),
    0,
  );
}

export function resolvePriorSprintHoursSource(sprintList = [], selectedSprint = null) {
  if (!Array.isArray(sprintList) || sprintList.length === 0) {
    return null;
  }

  const sorted = [...sprintList].sort(
    (a, b) => new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime(),
  );
  const selectedState = String(selectedSprint?.state || '').toLowerCase();
  const selectedId = String(selectedSprint?._id || selectedSprint?.id || '');

  if (selectedState === 'future') {
    const activeSprint = sorted.find((sprint) => {
      const state = String(sprint?.state || '').toLowerCase();
      return state === 'active' || state === 'current';
    });
    if (activeSprint && assigneesHaveAvailableHours(activeSprint.assignees)) {
      return activeSprint;
    }
  }

  const selectedIdx = selectedId
    ? sorted.findIndex((sprint) => String(sprint?._id) === selectedId)
    : -1;
  const startIdx = selectedIdx > 0 ? selectedIdx - 1 : sorted.length - 1;

  for (let i = startIdx; i >= 0; i -= 1) {
    if (assigneesHaveAvailableHours(sorted[i]?.assignees)) {
      return sorted[i];
    }
  }

  return null;
}

/** Team available total for standup — sum current plan only (never borrow another sprint/release). */
export function resolveTeamAvailableCapacityFallback(
  sprintList = [],
  selectedSprint = null,
  assignees = [],
  isHoursBased = false,
) {
  void sprintList;
  void selectedSprint;
  return sumAssigneeAvailableCapacity(assignees, isHoursBased);
}

/** Future and newly-started active sprints reuse prior sprint available hours until capacity is replanned. */
export function carryForwardActiveSprintAvailableHours(
  assignees = [],
  sprintList = [],
  selectedSprint = null,
) {
  if (!assignees?.length) {
    return assignees;
  }

  const selectedState = String(selectedSprint?.state || '').toLowerCase();
  const isFutureSprint = selectedState === 'future';

  // Active/closed sprints with any capacity defined — leave roster unchanged (SP projects).
  // Future sprints may have partial carry-forward (one assignee filled) — always merge per row.
  if (!isFutureSprint && assigneesHaveAvailableHours(assignees)) {
    return assignees;
  }

  const sourceSprint = resolvePriorSprintHoursSource(sprintList, selectedSprint);
  if (!sourceSprint?.assignees?.length) {
    return assignees;
  }

  const hoursByAssignee = new Map();
  sourceSprint.assignees.forEach((row) => {
    const key = String(row?.assignee || '')
      .trim()
      .toLowerCase();
    if (!key) {
      return;
    }
    hoursByAssignee.set(key, {
      availableHours: Number(row?.availableHours || 0),
      netAvailableCapacity: Number(row?.netAvailableCapacity ?? row?.availableHours ?? 0),
    });
  });

  return assignees.map((row) => {
    const prior = hoursByAssignee.get(
      String(row?.assignee || '')
        .trim()
        .toLowerCase(),
    );
    if (!prior) {
      return row;
    }
    const hasHours =
      row?.sprintOrReleaseUser === 'yes'
      || Number(row?.availableHours || 0) > 0
      || Number(row?.netAvailableCapacity || 0) > 0;
    if (hasHours) {
      return row;
    }
    return {
      ...row,
      availableHours: prior.availableHours,
      netAvailableCapacity: prior.netAvailableCapacity,
    };
  });
}

function sortReleasesForCarryForward(releaseList = []) {
  return [...releaseList].sort((a, b) => {
    const aStart = new Date(a?.startDate || a?.releaseDate || 0).getTime();
    const bStart = new Date(b?.startDate || b?.releaseDate || 0).getTime();
    if (aStart !== bStart) {
      return aStart - bStart;
    }
    return new Date(a?.releaseDate || 0).getTime() - new Date(b?.releaseDate || 0).getTime();
  });
}

function isFutureUnreleasedRelease(release) {
  const status = String(release?.status || '').toLowerCase();
  if (status !== 'unreleased') {
    return false;
  }
  if (!release?.startDate || Number.isNaN(new Date(release.startDate).getTime())) {
    return true;
  }
  const start = new Date(release.startDate).toISOString().split('T')[0];
  const today = new Date().toISOString().split('T')[0];
  return start > today;
}

export function resolvePriorReleaseHoursSource(releaseList = [], selectedRelease = null) {
  if (!Array.isArray(releaseList) || releaseList.length === 0) {
    return null;
  }

  const sorted = sortReleasesForCarryForward(releaseList);
  const selectedId = String(selectedRelease?._id || selectedRelease?.id || '');
  const selectedIdx = selectedId
    ? sorted.findIndex((release) => String(release?._id || release?.id) === selectedId)
    : -1;
  if (selectedIdx <= 0) {
    return null;
  }

  for (let i = selectedIdx - 1; i >= 0; i -= 1) {
    if (assigneesHaveAvailableHours(sorted[i]?.assignees)) {
      return sorted[i];
    }
  }

  return null;
}

/** Unreleased releases reuse prior release available hours until capacity is replanned (Kanban). */
export function carryForwardActiveReleaseAvailableHours(
  assignees = [],
  releaseList = [],
  selectedRelease = null,
) {
  if (!assignees?.length) {
    return assignees;
  }

  const selectedStatus = String(selectedRelease?.status || '').toLowerCase();
  const isFutureUnreleased = isFutureUnreleasedRelease(selectedRelease);

  if (selectedStatus !== 'unreleased') {
    return assignees;
  }

  if (!isFutureUnreleased && assigneesHaveAvailableHours(assignees)) {
    return assignees;
  }

  const sourceRelease = resolvePriorReleaseHoursSource(releaseList, selectedRelease);
  if (!sourceRelease?.assignees?.length) {
    return assignees;
  }

  const hoursByAssignee = new Map();
  sourceRelease.assignees.forEach((row) => {
    const key = String(row?.assignee || '')
      .trim()
      .toLowerCase();
    if (!key) {
      return;
    }
    hoursByAssignee.set(key, {
      availableHours: Number(row?.availableHours || 0),
      netAvailableCapacity: Number(row?.netAvailableCapacity ?? row?.availableHours ?? 0),
    });
  });

  return assignees.map((row) => {
    const prior = hoursByAssignee.get(
      String(row?.assignee || '')
        .trim()
        .toLowerCase(),
    );
    if (!prior) {
      return row;
    }
    const hasHours =
      row?.sprintOrReleaseUser === 'yes'
      || Number(row?.availableHours || 0) > 0
      || Number(row?.netAvailableCapacity || 0) > 0;
    if (hasHours) {
      return row;
    }
    return {
      ...row,
      availableHours: prior.availableHours,
      netAvailableCapacity: prior.netAvailableCapacity,
    };
  });
}

/** Last numeric sequence in sprint name, e.g. "PFNZ Sprint 39" -> 39 */
export function extractSprintSequenceNumber(nameOrLabel) {
  const matches = String(nameOrLabel || '').match(/\d+/g);
  if (!matches?.length) return 0;
  return Number(matches[matches.length - 1]);
}

function sprintSortTimestamp(sprint) {
  const raw = sprint?.startDate || sprint?.endDate || sprint?.completeDate;
  if (!raw) return 0;
  const ts = new Date(raw).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

/** Format sprint/release date for display; returns NA when Jira has not set dates (common for future sprints). */
export function formatStandupDisplayDate(dateValue) {
  if (dateValue === null || dateValue === undefined || dateValue === '') {
    return 'NA';
  }
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) {
    return 'NA';
  }
  return d.toLocaleDateString('en-IN');
}

const sprintDropdownStateRank = (state) => {
  const raw = String(state || '').toLowerCase();
  if (raw === 'future') return 0;
  if (raw === 'active' || raw === 'current') return 1;
  return 2;
};

const isClosedSprintState = (state) => {
  const raw = String(state || '').toLowerCase();
  return raw === 'closed' || raw === 'past' || raw === 'done';
};

const closedSprintSortTimestamp = (sprint) => {
  for (const field of ['completeDate', 'endDate', 'startDate']) {
    const raw = sprint?.[field];
    if (!raw) continue;
    const ts = new Date(raw).getTime();
    if (!Number.isNaN(ts)) return ts;
  }
  return 0;
};

const compareSprintsWithinState = (a, b, state) => {
  const aState = String(state || '').toLowerCase();
  if (isClosedSprintState(aState)) {
    const tsDiff = closedSprintSortTimestamp(b) - closedSprintSortTimestamp(a);
    if (tsDiff !== 0) return tsDiff;
    const aNum = extractSprintSequenceNumber(a.name || a.label);
    const bNum = extractSprintSequenceNumber(b.name || b.label);
    if (aNum !== bNum) return bNum - aNum;
    return String(a.name || a.label || '').localeCompare(String(b.name || b.label || ''));
  }

  const aNum = extractSprintSequenceNumber(a.name || a.label);
  const bNum = extractSprintSequenceNumber(b.name || b.label);
  if (aNum !== bNum) {
    if (aState === 'future') {
      return bNum - aNum;
    }
    return aNum - bNum;
  }
  const aTs = sprintSortTimestamp(a);
  const bTs = sprintSortTimestamp(b);
  if (aState === 'future') {
    return bTs - aTs;
  }
  return aTs - bTs;
};

/** Standup sprint dropdown: future (desc # so next sprint sits above active) → active → closed (desc by completion date). */
export function sortStandupSprintDropdownList(sprintList = []) {
  return [...sprintList].sort((a, b) => {
    const rankDiff = sprintDropdownStateRank(a.state) - sprintDropdownStateRank(b.state);
    if (rankDiff !== 0) return rankDiff;
    return compareSprintsWithinState(a, b, a.state);
  });
}

/** True when at least one capacity row has a real assignee name. */
export function hasValidCapacityAssigneeRows(assignees) {
  return (assignees || []).some((a) => {
    const n = String(a?.assignee || '').trim();
    return n && !isBucketAssigneeName(n);
  });
}

/** Prefer sprint/release saved capacity (including explicit 0) over roster defaults when merging rows. */
function mergeCapacityAvailableHours(cur = {}, row = {}) {
  const curSaved = cur.sprintOrReleaseUser === 'yes';
  const rowSaved = row.sprintOrReleaseUser === 'yes';
  if (curSaved && !rowSaved) {
    return Number(cur.availableHours ?? 0);
  }
  if (rowSaved && !curSaved) {
    return Number(row.availableHours ?? 0);
  }
  return Math.max(Number(cur.availableHours ?? 0), Number(row.availableHours ?? 0));
}

function mergeCapacityNetAvailable(cur = {}, row = {}) {
  const curSaved = cur.sprintOrReleaseUser === 'yes';
  const rowSaved = row.sprintOrReleaseUser === 'yes';
  if (curSaved && !rowSaved) {
    return Number(cur.netAvailableCapacity ?? 0);
  }
  if (rowSaved && !curSaved) {
    return Number(row.netAvailableCapacity ?? 0);
  }
  return Math.max(Number(cur.netAvailableCapacity ?? 0), Number(row.netAvailableCapacity ?? 0));
}

/** Overlay user-saved availability onto API assignees (release carry-forward safe). */
export function applySavedCapacityAvailabilityToAssignees(assignees = [], savedRows = []) {
  if (!Array.isArray(assignees) || !Array.isArray(savedRows) || savedRows.length === 0) {
    return assignees;
  }
  const byNorm = new Map();
  for (const row of savedRows) {
    const name = String(row?.name || row?.assignee || '').trim();
    if (!name) continue;
    if (!isAssigneeOnCapacityPlan(row)) continue;
    byNorm.set(name.toLowerCase(), row);
  }
  return assignees.map((assignee) => {
    const key = String(assignee?.assignee || '').trim().toLowerCase();
    const saved = byNorm.get(key);
    if (!saved) {
      return assignee;
    }
    const savedHours = Number(saved.availableHours ?? assignee.availableHours ?? 0);
    const savedNet = Number(
      saved.netAvailableCapacity ?? saved.availableHours ?? assignee.netAvailableCapacity ?? 0,
    );
    return {
      ...assignee,
      availableHours: savedHours,
      netAvailableCapacity: savedNet,
      role: saved.role || assignee.role || '',
      email: String(saved.email ?? assignee.email ?? '').trim(),
      allocationType: saved.allocationType ?? assignee.allocationType ?? '',
      leaves: Number(saved.leaves ?? assignee.leaves ?? 0),
      billingRate: Number(saved.billingRate ?? assignee.billingRate ?? 0),
      sprintOrReleaseUser: 'yes',
      modified: saved.modified === true || assignee.modified === true,
      planLockedLocally:
        saved.planLockedLocally === 'yes'
        || assignee.planLockedLocally === 'yes'
          ? 'yes'
          : (assignee.planLockedLocally === 'no' ? 'no' : assignee.planLockedLocally),
    };
  });
}

/** Keep grid availability when Jira sync refreshed allocation but wiped saved available hours. */
export function mergeLocalCapacityAvailabilityOverApi(assignees = [], localRows = []) {
  if (!Array.isArray(assignees) || !Array.isArray(localRows) || localRows.length === 0) {
    return assignees;
  }
  const localByNorm = new Map();
  for (const row of localRows) {
    const name = String(row?.name || row?.assignee || '').trim();
    if (!name) continue;
    localByNorm.set(name.toLowerCase(), row);
  }
  return assignees.map((assignee) => {
    const key = String(assignee?.assignee || '').trim().toLowerCase();
    const local = localByNorm.get(key);
    if (!local) {
      return assignee;
    }
    if (!isAssigneeOnCapacityPlan(local)) {
      return assignee;
    }
    const localH = Number(local.availableHours ?? 0);
    const localNet = Number(local.netAvailableCapacity ?? localH);
    const apiH = Number(assignee?.availableHours ?? 0);
    const apiNet = Number(assignee?.netAvailableCapacity ?? 0);
    const localHasCapacity = localH > 0 || localNet > 0;
    const apiWiped = (localH > 0 && apiH === 0) || (localNet > 0 && apiNet === 0);
    if (!localHasCapacity || !apiWiped) {
      return assignee;
    }
    return {
      ...assignee,
      availableHours: localH,
      netAvailableCapacity: localNet,
      role: local.role || assignee.role || '',
      email: String(local.email ?? assignee.email ?? '').trim(),
      allocationType: local.allocationType ?? assignee.allocationType ?? '',
      leaves: Number(local.leaves ?? assignee.leaves ?? 0),
      billingRate: Number(local.billingRate ?? assignee.billingRate ?? 0),
      sprintOrReleaseUser: 'yes',
    };
  });
}

/**
 * Capacity grid should show the same roster as Standup: merge Sprint/Release assignees with project userList.
 * When addMissingUserListMembers is false (saved Add Users plan exists), only enrich existing rows — do not dump full roster.
 */
export function mergeCapacityAssigneesWithUserList({
  assignees = [],
  userList = [],
  existingRoles = [],
  addMissingUserListMembers = true,
} = {}) {
  const byKey = new Map();

  const pickPlanLockedLocally = (...rows) => (
    rows.some((r) => r?.planLockedLocally === 'yes' || r?.planLockedLocally === true) ? 'yes' : 'no'
  );
  const pickModified = (...rows) => rows.some((r) => r?.modified === true);

  const ensure = (row) => {
    const name = String(row?.assignee || '').trim();
    if (!name) {
      return null;
    }
    if (isUnassignedBucketName(name)) {
      const hasAllocation =
        Number(row?.allocatedHours ?? 0) > 0
        || Number(row?.allocatedStoryPoints ?? row?.allocatedHours ?? 0) > 0
        || Number(row?.tickets ?? 0) > 0
        || row?.sprintOrReleaseUser === 'yes'
        || Number(row?.availableHours || 0) > 0;
      if (!hasAllocation) {
        return null;
      }
      const key = 'unassigned';
      if (!byKey.has(key)) {
        byKey.set(key, {
          _id: row._id || null,
          assignee: 'Unassigned',
          email: String(row.email || '').trim(),
          role: row.role || '',
          allocationType: row.allocationType || '',
          availableHours: Number(row.availableHours || 0),
          allocatedHours: Number(row.allocatedHours || 0),
          allocatedStoryPoints: Number(row.allocatedStoryPoints ?? row.allocatedHours ?? 0),
          tickets: Number(row.tickets || 0),
          initialPlannedAllocation:
            row.initialPlannedAllocation !== null
            && row.initialPlannedAllocation !== undefined
            && Number.isFinite(Number(row.initialPlannedAllocation))
              ? Number(row.initialPlannedAllocation)
              : undefined,
          netAvailableCapacity: Number(row.netAvailableCapacity || 0),
          billingRate: Number(row.billingRate || 0),
          holiday: Number(row.holiday || 0),
          leaves: Number(row.leaves || 0),
          addedManually: row.addedManually === 'yes' ? 'yes' : 'no',
          presentInPlan: row.presentInPlan === 'no' ? 'no' : 'yes',
          sprintOrReleaseUser: row.sprintOrReleaseUser === 'yes' ? 'yes' : 'no',
          planLockedLocally: pickPlanLockedLocally(row),
          modified: pickModified(row),
          addUserModalLocked: Number(row.tickets || 0) > 0 ? 'yes' : 'no',
        });
      } else {
        const cur = byKey.get(key);
        const tickets = Math.max(Number(cur.tickets || 0), Number(row.tickets || 0));
        byKey.set(key, {
          ...cur,
          email: cur.email || String(row.email || '').trim(),
          role: cur.role || row.role || '',
          allocationType: cur.allocationType || row.allocationType || '',
          availableHours: mergeCapacityAvailableHours(cur, row),
          allocatedHours: Math.max(Number(cur.allocatedHours || 0), Number(row.allocatedHours || 0)),
          allocatedStoryPoints: Math.max(
            Number(cur.allocatedStoryPoints || 0),
            Number(row.allocatedStoryPoints ?? row.allocatedHours ?? 0),
          ),
          tickets,
          initialPlannedAllocation:
            cur.initialPlannedAllocation !== null
            && cur.initialPlannedAllocation !== undefined
            && Number.isFinite(Number(cur.initialPlannedAllocation))
              ? Number(cur.initialPlannedAllocation)
              : row.initialPlannedAllocation !== null
                && row.initialPlannedAllocation !== undefined
                && Number.isFinite(Number(row.initialPlannedAllocation))
                ? Number(row.initialPlannedAllocation)
                : undefined,
          netAvailableCapacity: mergeCapacityNetAvailable(cur, row),
          billingRate: Math.max(Number(cur.billingRate || 0), Number(row.billingRate || 0)),
          presentInPlan: pickCapacityPresentInPlan(cur.presentInPlan, row.presentInPlan),
          planLockedLocally: pickPlanLockedLocally(cur, row),
          modified: pickModified(cur, row),
          addUserModalLocked: tickets > 0 ? 'yes' : 'no',
        });
      }
      return byKey.get(key);
    }
    if (isBucketAssigneeName(name)) {
      return null;
    }
    const key = name.toLowerCase();
    if (!byKey.has(key)) {
      byKey.set(key, {
        _id: row._id || null,
        assignee: name,
        email: String(row.email || '').trim(),
        role: row.role || '',
        allocationType: row.allocationType || '',
        availableHours: Number(row.availableHours || 0),
        allocatedHours: Number(row.allocatedHours || 0),
        allocatedStoryPoints: Number(row.allocatedStoryPoints ?? row.allocatedHours ?? 0),
        tickets: Number(row.tickets || 0),
        initialPlannedAllocation:
          row.initialPlannedAllocation !== null
          && row.initialPlannedAllocation !== undefined
          && Number.isFinite(Number(row.initialPlannedAllocation))
            ? Number(row.initialPlannedAllocation)
            : undefined,
        netAvailableCapacity: Number(row.netAvailableCapacity || 0),
        billingRate: Number(row.billingRate || 0),
        holiday: Number(row.holiday || 0),
        leaves: Number(row.leaves || 0),
        addedManually: row.addedManually === 'yes' ? 'yes' : 'no',
        presentInPlan: row.presentInPlan === 'no' ? 'no' : 'yes',
        sprintOrReleaseUser: row.sprintOrReleaseUser === 'yes' ? 'yes' : 'no',
        planLockedLocally: pickPlanLockedLocally(row),
        modified: pickModified(row),
        addUserModalLocked: Number(row.tickets || 0) > 0 ? 'yes' : 'no',
      });
    } else {
      const cur = byKey.get(key);
      const tickets = Math.max(Number(cur.tickets || 0), Number(row.tickets || 0));
      byKey.set(key, {
        ...cur,
        email: cur.email || String(row.email || '').trim(),
        role: cur.role || row.role || '',
        allocationType: cur.allocationType || row.allocationType || '',
        availableHours: mergeCapacityAvailableHours(cur, row),
        allocatedHours: Math.max(Number(cur.allocatedHours || 0), Number(row.allocatedHours || 0)),
        allocatedStoryPoints: Math.max(
          Number(cur.allocatedStoryPoints || 0),
          Number(row.allocatedStoryPoints ?? row.allocatedHours ?? 0),
        ),
        tickets,
        initialPlannedAllocation:
          cur.initialPlannedAllocation !== null
          && cur.initialPlannedAllocation !== undefined
          && Number.isFinite(Number(cur.initialPlannedAllocation))
            ? Number(cur.initialPlannedAllocation)
            : row.initialPlannedAllocation !== null
              && row.initialPlannedAllocation !== undefined
              && Number.isFinite(Number(row.initialPlannedAllocation))
              ? Number(row.initialPlannedAllocation)
              : undefined,
        netAvailableCapacity: mergeCapacityNetAvailable(cur, row),
        billingRate: Math.max(Number(cur.billingRate || 0), Number(row.billingRate || 0)),
        presentInPlan: pickCapacityPresentInPlan(cur.presentInPlan, row.presentInPlan),
        planLockedLocally: pickPlanLockedLocally(cur, row),
        modified: pickModified(cur, row),
        addUserModalLocked: tickets > 0 ? 'yes' : 'no',
      });
    }
    return byKey.get(key);
  };

  for (const a of assignees || []) {
    ensure(a);
  }

  if (addMissingUserListMembers) {
    for (const u of userList || []) {
      const displayName = String(u?.displayName || '').trim();
      if (!displayName || isBucketAssigneeName(displayName)) {
        continue;
      }
      const key = displayName.toLowerCase();
      const existing = byKey.get(key);
      if (existing && existing.presentInPlan === 'no') {
        continue;
      }
      const roleFromUser = u?.role || '';
      const roleRate = (existingRoles || []).find((r) => r.role === roleFromUser);
      ensure({
        assignee: displayName,
        email: String(u?.email || u?.mail || '').trim(),
        role: roleFromUser,
        availableHours: 0,
        allocatedHours: 0,
        allocatedStoryPoints: 0,
        billingRate: roleRate?.rate || 0,
        addedManually: 'no',
        sprintOrReleaseUser: 'no',
      });
    }
  }

  return Array.from(byKey.values());
}

/**
 * Developer names for Standup filter: capacity plan assignees (presentInPlan yes) + optional Unassigned.
 * Project userList is only merged when includeProjectRoster is true (no saved capacity assignees yet).
 */
export function collectStandupDeveloperNames(
  capacityAssignees,
  userList,
  { injectUnassigned = false, includeProjectRoster = false, isHoursBased = null } = {},
) {
  const names = new Set();
  for (const a of capacityAssignees || []) {
    const n = String(a?.assignee || '').trim();
    if (!n) {
      continue;
    }
    if (isUnassignedBucketName(n)) {
      if (shouldShowUnassignedCapacityApiAssignee(a, isHoursBased)) {
        names.add('Unassigned');
      }
      continue;
    }
    if (isBucketAssigneeName(n)) {
      continue;
    }
    names.add(n);
  }
  if (includeProjectRoster) {
    for (const u of userList || []) {
      const n = String(u?.displayName || '').trim();
      if (n && !isBucketAssigneeName(n)) {
        names.add(n);
      }
    }
  }
  if (injectUnassigned) {
    names.add('Unassigned');
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}